// tickets.api.ts — version complète Phase 5-B
// Remplace apps/frontend/src/modules/tickets/api/tickets.api.ts

import api from '@/lib/axios'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TicketCategory {
  id: string; name: string; color?: string; icon?: string; isActive: boolean
  _count?: { tickets: number }
}

export interface Ticket {
  id: string; number: number; title: string; description: string
  status: string; priority: string
  categoryId?: string; category?: TicketCategory
  // Phase 5 corrigé : creatorId (pas createdById), assigneeId (pas assignedToId)
  creatorId: string;  creator:  { id: string; firstName: string; lastName: string; avatarUrl?: string }
  assigneeId?: string; assignee?: { id: string; firstName: string; lastName: string; avatarUrl?: string }
  application?: string; resolution?: string
  firstResponseAt?: string; resolvedAt?: string; closedAt?: string
  dueDate?: string
  slaBreached: boolean  // (pas isSlaViolated)
  satisfactionScore?: number | null
  satisfactionNote?:  string | null
  internalNotes?: string; tags: string[]
  // Informations de signalement (saisies à la création)
  applicationName?: string | null; environment?: string | null; version?: string | null
  stepsToReproduce?: string | null; expectedBehavior?: string | null; actualBehavior?: string | null
  // Inclus par GET /tickets/:id
  comments?: TicketComment[]
  createdAt: string; updatedAt: string
  _count?: { comments: number; attachments: number }
  slaInfo?: {
    firstResponseRemainingMs?: number | null
    resolutionRemainingMs?:    number | null
    firstResponseStatus: 'OK' | 'WARNING' | 'VIOLATED' | 'MET'
    resolutionStatus:    'OK' | 'WARNING' | 'VIOLATED' | 'MET'
  }
  // Phase 5-B
  linkedTickets?: TicketLink[]
  linkedFrom?:    TicketLink[]
}

export interface TicketLink {
  id: string; linkType: string
  linkedTicket?: Partial<Ticket>
  mainTicket?:   Partial<Ticket>
}

export interface TicketComment {
  id: string; ticketId: string; content: string; isInternal: boolean
  authorId: string; author: { id: string; firstName: string; lastName: string; avatarUrl?: string }
  createdAt: string
}

// ─── API ──────────────────────────────────────────────────────────────────────

const R = (r: any) => r.data.data

export const ticketsApi = {
  // Dashboard
  getDashboard:     ()                 => api.get('/tickets/dashboard').then(R),
  getSlaStats:      ()                 => api.get('/tickets/sla-stats').then(R),

  // Catégories
  listCategories:   ()                 => api.get('/tickets/categories').then(R),
  createCategory:   (data: any)        => api.post('/tickets/categories', data).then(R),
  deleteCategory:   (id: string)       => api.delete(`/tickets/categories/${id}`).then(R),

  // Tickets
  listTickets:      (p?: any)          => api.get('/tickets', { params: p }).then(R),
  getTicket:        (id: string)       => api.get(`/tickets/${id}`).then(R),
  createTicket:     (data: any)        => api.post('/tickets', data).then(R),
  updateTicket:     (id: string, d: any) => api.patch(`/tickets/${id}`, d).then(R),
  assignTicket:     (id: string, d: any) => api.patch(`/tickets/${id}/assign`, d).then(R),
  resolveTicket:    (id: string, d: any) => api.patch(`/tickets/${id}/resolve`, d).then(R),
  closeTicket:      (id: string)       => api.patch(`/tickets/${id}/close`).then(R),
  reopenTicket:     (id: string)       => api.patch(`/tickets/${id}/reopen`).then(R),

  // Commentaires
  addComment:       (id: string, d: any) => api.post(`/tickets/${id}/comments`, d).then(R),

  // Satisfaction (Phase 5-B)
  submitSatisfaction: (id: string, d: { score: number; note?: string }) =>
    api.post(`/tickets/${id}/rate`, d).then(R),

  // Sous-tickets (Phase 5-B)
  getSubTickets:    (id: string)       => api.get(`/tickets/${id}/sub-tickets`).then(R),
  createSubTicket:  (id: string, d: any) => api.post(`/tickets/${id}/sub-tickets`, d).then(R),

  // Liens (Phase 5-B)
  linkTickets:      (id: string, d: { linkedTicketId: string; linkType: string }) =>
    api.post(`/tickets/${id}/links`, d).then(R),
  unlinkTickets:    (id: string, linkedId: string) =>
    api.delete(`/tickets/${id}/links/${linkedId}`).then(R),

  // Fusion (Phase 5-B)
  mergeTickets:     (targetId: string, d: { sourceId: string; reason?: string }) =>
    api.post(`/tickets/${targetId}/merge`, d).then(R),
}

// ─────────────────────────────────────────────────────────────────────────────
//  Orgchart types & API (inchangés)
// ─────────────────────────────────────────────────────────────────────────────

export interface OrgPosition {
  id: string; title: string; level: number; order: number
  isVacant: boolean; parentId?: string; departmentId?: string
  employeeId?: string
  employee?: {
    id: string; jobTitle?: string
    user: { firstName: string; lastName: string; avatarUrl?: string; email: string }
  }
  department?: { id: string; name: string }
  children?: OrgPosition[]
}

export const orgchartApi = {
  getTree:         (params?: { departmentId?: string; showVacant?: boolean }) =>
    api.get('/orgchart', { params }).then(R),
  getFlat:         (departmentId?: string) =>
    api.get('/orgchart/flat', { params: { departmentId } }).then(R),
  getStats:        ()                   => api.get('/orgchart/stats').then(R),
  getPosition:     (id: string)         => api.get(`/orgchart/${id}`).then(R),
  createPosition:  (data: any)          => api.post('/orgchart', data).then(R),
  updatePosition:  (id: string, d: any) => api.patch(`/orgchart/${id}`, d).then(R),
  movePosition:    (id: string, newParentId: string) =>
    api.patch(`/orgchart/${id}/move`, { newParentId }).then(R),
  deletePosition:  (id: string)         => api.delete(`/orgchart/${id}`).then(R),
}
